<script setup lang="ts">
import { computed } from 'vue';
import Send from '@primeicons/vue/send';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import type { V2CurrentPositionRow } from '@gpx-spa/queries/src/getAccountCurrentPositionsV2';
import type { V2ShipmentDevice } from '@gpx-spa/queries/src/getAccountShipmentV2';
import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';
import { displayExceptionEventTitle } from '@/utils/shipment-exception-display';
import { namedTrackerLabel } from '@/utils/named-tracker-set';
import {
  formatRelativeTimestamp,
  formatShipmentDateTime,
  isStaleReport,
} from '@/utils/shipment-detail-format';
import { SHIPMENT_DETAIL_TRIM } from '@/components/shipment/detail/shipment-detail-trim';
import { shipmentDetailCardPt } from '@/components/shipment/detail/shipment-detail-card-pt';

const props = defineProps<{
  device: V2ShipmentDevice | null;
  position: V2CurrentPositionRow | null;
  isLoading?: boolean;
  exceptions?: ShipmentExceptionRow[];
}>();

const cardPt = shipmentDetailCardPt;

const deviceName = computed(() =>
  props.device ? namedTrackerLabel(props.device) : '—',
);
const serialDisplay = computed(() => props.device?.serial?.trim() || '—');

const lastReportUnix = computed(() => props.position?.timestamp ?? null);
const lastReportRelative = computed(() =>
  formatRelativeTimestamp(lastReportUnix.value),
);
const showStaleAlert = computed(
  () =>
    !SHIPMENT_DETAIL_TRIM.deviceStaleAlert &&
    isStaleReport(lastReportUnix.value),
);

const batteryDisplay = computed(() => {
  const value = props.position?.battery;
  if (value == null) return '—';
  return `${value}%`;
});

const speedDisplay = computed(() => {
  const value = props.position?.speed;
  if (value == null) return '—';
  return `${value} mph`;
});

const temperatureDisplay = computed(() => {
  const value = props.position?.temperature;
  if (value == null) return '—';
  return `${value}°C`;
});

const humidityDisplay = computed(() => {
  const value = props.position?.humidity;
  if (value == null) return '—';
  return `${value}%`;
});

const deviceExceptions = computed(() => props.exceptions ?? []);
</script>

<template>
  <Card :pt="cardPt">
    <template #content>
      <div class="mb-2 flex items-center gap-2">
        <Send class="text-neutral-400" aria-hidden="true" />
        <h3 class="text-sm font-semibold text-neutral-900">
          Device Information
        </h3>
      </div>

      <div
        v-if="showStaleAlert"
        class="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
      >
        Last reported {{ lastReportRelative }}. Device may be powered off or out
        of range.
      </div>

      <div v-if="!device" class="text-sm text-neutral-400">
        No device assigned to this shipment.
      </div>

      <dl v-else class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        <div>
          <dt class="mb-0.5 text-xs text-neutral-500">Device Name</dt>
          <dd
            class="flex flex-wrap items-center gap-1 text-sm font-medium text-neutral-900"
          >
            <span>{{ deviceName }}</span>
            <Tag
              v-for="row in deviceExceptions"
              :key="row.id"
              :value="displayExceptionEventTitle(row.type)"
              severity="danger"
              class="!text-[10px] !font-semibold tracking-wide"
              :pt="{ root: { class: '!px-1.5 !py-0.5' } }"
            />
          </dd>
        </div>
        <div>
          <dt class="mb-0.5 text-xs text-neutral-500">Serial</dt>
          <dd class="font-mono text-sm font-medium text-neutral-900">
            {{ serialDisplay }}
          </dd>
        </div>
        <div>
          <dt class="mb-0.5 text-xs text-neutral-500">Battery</dt>
          <dd class="text-sm font-medium text-neutral-900">
            {{ batteryDisplay }}
          </dd>
        </div>
        <div>
          <dt class="mb-0.5 text-xs text-neutral-500">Last Report</dt>
          <dd
            class="text-sm font-medium text-neutral-900"
            :title="
              formatShipmentDateTime(
                lastReportUnix
                  ? new Date(lastReportUnix * 1000).toISOString()
                  : null,
              )
            "
          >
            {{ lastReportRelative }}
          </dd>
        </div>
        <div v-if="!SHIPMENT_DETAIL_TRIM.deviceSpeed">
          <dt class="mb-0.5 text-xs text-neutral-500">Speed</dt>
          <dd class="text-sm font-medium text-neutral-900">
            {{ speedDisplay }}
          </dd>
        </div>
        <div v-if="!SHIPMENT_DETAIL_TRIM.deviceTemperature">
          <dt class="mb-0.5 text-xs text-neutral-500">Temperature</dt>
          <dd class="text-sm font-medium text-neutral-900">
            {{ temperatureDisplay }}
          </dd>
        </div>
        <div v-if="!SHIPMENT_DETAIL_TRIM.deviceHumidity">
          <dt class="mb-0.5 text-xs text-neutral-500">Humidity</dt>
          <dd class="text-sm font-medium text-neutral-900">
            {{ humidityDisplay }}
          </dd>
        </div>
      </dl>
    </template>
  </Card>
</template>
